import type { V2ShipmentDetail } from '@gpx-spa/queries/src/getAccountShipmentV2';
import { formatTime } from '@/filters';
import { exceptionIconsHtmlFromFlags } from '@/utils/shipment-exception-display';
import {
  isShipmentDeliveredLate,
  SHIPMENT_DISPLAY_STATUS,
  mapBadgeCssClass,
  mapClusterDotCssClass,
  mapFilterKey,
  type ShipmentMapFilterKey,
} from '@/utils/shipment-status-ui';
import {
  parseListMarkerShipmentId,
  resolveOriginDestinationStops,
} from './shipment-list-map-layers';

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function shipmentDisplayName(shipment: V2ShipmentDetail): string {
  return shipment.name?.trim() || `Shipment ${shipment.id}`;
}

function stopAddressText(stop: { name?: string | null } | null | undefined): string {
  const name = stop?.name?.trim();
  return name || '—';
}

function wrapTooltip(body: string, wide = false): string {
  const root = wide
    ? 'gpx-shipment-map-tooltip gpx-shipment-map-tooltip--wide'
    : 'gpx-shipment-map-tooltip';
  return `<div class="${root}">${body}</div>`;
}

function statusBadgeHtml(
  label: string,
  statusKey: ShipmentMapFilterKey,
): string {
  return `<span class="${mapBadgeCssClass(statusKey)}">${escapeHtml(label)}</span>`;
}

function statusBadgesHtml(shipment: V2ShipmentDetail, now?: Date): string {
  const filterKey = mapFilterKey(shipment, now);
  const label = shipment.status ?? SHIPMENT_DISPLAY_STATUS.Draft;
  const badges = [statusBadgeHtml(label, filterKey)];
  if (isShipmentDeliveredLate(shipment)) {
    badges.push(
      statusBadgeHtml(
        SHIPMENT_DISPLAY_STATUS.Late,
        SHIPMENT_DISPLAY_STATUS.Late,
      ),
    );
  }
  return badges.join('');
}

function formatEta(endAt: string | null | undefined): string {
  if (!endAt) return '—';
  return formatTime(endAt, 'M/d h:mm aa');
}

function exceptionsLine(shipment: V2ShipmentDetail): string {
  return exceptionIconsHtmlFromFlags(shipment.exception_flags);
}

export function buildShipmentEndpointTooltipHtml(
  shipment: V2ShipmentDetail,
  endpoint: 'O' | 'D',
): string {
  const { origin, destination } = resolveOriginDestinationStops(shipment.stops ?? []);
  const stop = endpoint === 'O' ? origin : destination;
  if (!stop) {
    return wrapTooltip(
      `<div class="gpx-shipment-map-tooltip__title">${escapeHtml(shipmentDisplayName(shipment))}</div>`,
    );
  }

  const role = endpoint === 'O' ? 'Origin' : 'Destination';
  const address = stopAddressText(stop);

  const body = [
    `<div class="gpx-shipment-map-tooltip__title">${escapeHtml(shipmentDisplayName(shipment))}</div>`,
    `<div class="gpx-shipment-map-tooltip__meta-row">${statusBadgesHtml(shipment)}<span class="gpx-shipment-map-tooltip__role">${escapeHtml(role)}</span></div>`,
    `<div class="gpx-shipment-map-tooltip__address">${escapeHtml(address)}</div>`,
    exceptionsLine(shipment),
  ].join('');
  return wrapTooltip(body);
}

export function buildShipmentArcTooltipHtml(shipment: V2ShipmentDetail): string {
  const { origin, destination } = resolveOriginDestinationStops(shipment.stops ?? []);
  const originName = stopAddressText(origin);
  const destName = stopAddressText(destination);

  const carrier = shipment.carrier?.trim();
  const carrierLine = carrier
    ? `<div class="gpx-shipment-map-tooltip__carrier">${escapeHtml(carrier)}</div>`
    : '';

  const body = [
    `<div class="gpx-shipment-map-tooltip__title">${escapeHtml(shipmentDisplayName(shipment))}</div>`,
    `<div class="gpx-shipment-map-tooltip__badge-wrap">${statusBadgesHtml(shipment)}</div>`,
    `<div class="gpx-shipment-map-tooltip__route">`,
    `<span class="gpx-shipment-map-tooltip__route-end">${escapeHtml(originName)}</span>`,
    `<span class="gpx-shipment-map-tooltip__route-arrow">→</span>`,
    `<span class="gpx-shipment-map-tooltip__route-end">${escapeHtml(destName)}</span>`,
    `</div>`,
    carrierLine,
    `<div class="gpx-shipment-map-tooltip__eta">ETA: ${escapeHtml(formatEta(shipment.end_at ?? null))}</div>`,
    exceptionsLine(shipment),
  ].join('');
  return wrapTooltip(body, true);
}

export type ClusterStatusRow = {
  filterKey: ShipmentMapFilterKey;
  label: string;
  count: number;
};

export function aggregateClusterStatusRows(
  childMarkerIds: string[] | undefined,
  shipmentsById: Map<number, V2ShipmentDetail>,
): { shipmentCount: number; rows: ClusterStatusRow[] } {
  const shipmentIds = new Set<number>();
  for (const markerId of childMarkerIds ?? []) {
    const id = parseListMarkerShipmentId(markerId);
    if (id != null) shipmentIds.add(id);
  }

  const byFilterKey = new Map<ShipmentMapFilterKey, { label: string; count: number }>();

  for (const id of shipmentIds) {
    const shipment = shipmentsById.get(id);
    if (!shipment) continue;
    const filterKey = mapFilterKey(shipment);
    const label = shipment.status ?? filterKey;
    const existing = byFilterKey.get(filterKey);
    if (existing) {
      existing.count += 1;
    } else {
      byFilterKey.set(filterKey, { label, count: 1 });
    }
  }

  const rows: ClusterStatusRow[] = [...byFilterKey.entries()]
    .map(([filterKey, { label, count }]) => ({ filterKey, label, count }))
    .sort((a, b) => b.count - a.count);

  return { shipmentCount: shipmentIds.size, rows };
}

export function buildShipmentClusterTooltipHtml(
  childMarkerIds: string[] | undefined,
  shipmentsById: Map<number, V2ShipmentDetail>,
): string {
  const { shipmentCount, rows } = aggregateClusterStatusRows(
    childMarkerIds,
    shipmentsById,
  );
  if (shipmentCount === 0) {
    return wrapTooltip('<div class="gpx-shipment-map-tooltip__muted">Cluster</div>');
  }

  const title =
    shipmentCount === 1
      ? '1 shipment'
      : `${shipmentCount} shipments`;

  const segments = rows
    .map((row) => {
      const pct = Math.round((row.count / shipmentCount) * 100);
      return `<div class="gpx-shipment-map-tooltip__cluster-row">
        <span class="${mapClusterDotCssClass(row.filterKey)}"></span>
        <span>${escapeHtml(row.label)}: <strong>${row.count}</strong> (${pct}%)</span>
      </div>`;
    })
    .join('');

  const body = [
    `<div class="gpx-shipment-map-tooltip__title--cluster">${escapeHtml(title)}</div>`,
    segments,
  ].join('');
  return wrapTooltip(body, true);
}
